import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { CopyJustification } from 'types/constants';
import { cn } from 'utils/styles';
import { CalloutGridCardProps } from './types';

/**
 * Props for the CalloutGridCardContent component
 */
interface CalloutGridCardContentProps {
  sys?: { id: string };
  card: CalloutGridCardProps;
  copyJustification?: CopyJustification;
  isLinkedCard: boolean;
}

/**
 * Renders the text content for a CalloutGrid card
 * Handles headline, copy, attribution, and justification
 */
const CalloutGridCardContent = ({ sys, card, copyJustification, isLinkedCard }: CalloutGridCardContentProps) => {
  const copyWrapperClasses = cn(
    copyJustification === CopyJustification.Center ? 'sm:text-center' : 'text-left',
    'sm:mt-4 flex flex-col gap-2',
  );

  // CalloutGrid has no section-level heading, so each card's headline is the
  // top-level heading for the section and renders as <h2>. When both fields
  // are populated, the small headline is the sub-heading and renders as <h3>.
  const smallHeadlineLevel: 'h2' | 'h3' = card.headline ? 'h3' : 'h2';

  return (
    <div className={copyWrapperClasses}>
      {card.headline && (
        <div
          className={cn(
            'font-sans font-semibold text-heading-sm [&_a]:underline',
            isLinkedCard && 'relative flex flex-col items-center pr-3 underline',
          )}
          {...liveUpdateAttrs(sys?.id, 'headline')}>
          <RichTextHeadline headline={card.headline} level="h2" />
        </div>
      )}
      {card.headlineSmallRte && (
        <div
          className={cn('font-sans font-semibold text-body-sans', isLinkedCard && 'underline')}
          {...liveUpdateAttrs(sys?.id, 'headlineSmallRte')}>
          <RichTextHeadline headline={card.headlineSmallRte} level={smallHeadlineLevel} />
        </div>
      )}
      {card.copy && (
        <div className="font-serif text-body-serif" {...liveUpdateAttrs(sys?.id, 'copy')}>
          <RichTextCopy copy={card.copy} ySpacing="none" />
        </div>
      )}
      {card.attributionRte && (
        <div className="font-sans font-medium" {...liveUpdateAttrs(sys?.id, 'attributionRte')}>
          &mdash; <RichTextCopy copy={card.attributionRte} ySpacing="none" inline />
        </div>
      )}
    </div>
  );
};

export default CalloutGridCardContent;
